/**
 * Scalable Theme Configuration
 * 
 * This file defines themes using properties that are computed into CSS variables.
 * This approach scales to hundreds of themes without requiring code changes.
 * 
 * To add a new theme, just add an entry to themeRegistry - no other code changes needed!
 */

import { CYDECORE_CSS_VAR_MAP } from "./cydecore-theme-tokens"

export type ThemeCategory = 'dark' | 'light';
export type ColorScheme = 'slate' | 'blue' | 'gray' | 'indigo' | 'custom';

/**
 * Theme Definition
 * 
 * Themes are defined by properties that get computed into CSS variables.
 * This allows infinite scalability - add 1000 themes with just data, no code.
 */
export interface ThemeDefinition {
  id: string;
  name: string;
  category: ThemeCategory;
  colorScheme: ColorScheme;
  // Primary color properties (computed into accent colors)
  primaryHue: number;           // 0-360 (HSL hue)
  primarySaturation: number;    // 0-100 (HSL saturation %)
  primaryLightness: number;     // 0-100 (HSL lightness %)
  // Optional overrides for specific computed values
  overrides?: {
    background?: string;
    backgroundGradient?: string;
    pageBackgroundGradient?: string;
    card?: string;
    foreground?: string;
    foregroundMuted?: string;
    foregroundSubtle?: string;
    border?: string;
    borderSubtle?: string;
    borderStrong?: string;
    accent?: string;
    accentHover?: string;
    accentMuted?: string;
    accentMutedForeground?: string;
    popover?: string;
    input?: string;
    muted?: string;
    success?: string;
    warning?: string;
    error?: string;
    warningBackground?: string;
    cardHover?: string;
    headerFooter?: string;
    /** Extra `--*` CSS variables merged after computed theme vars (e.g. CYDEcore token pack). */
    [key: string]: string | undefined;
  };
  /** Appended to computed CSS variables (overrides same keys). */
  cssVarExtras?: Record<string, string>;
}

/**
 * Theme Registry
 * 
 * Add themes here - they automatically work everywhere!
 * No need to update utility functions or CSS for each theme.
 */
export const themeRegistry: Record<string, ThemeDefinition> = {
  'dark': {
    id: 'dark',
    name: 'Dark',
    category: 'dark',
    colorScheme: 'slate',
    primaryHue: 220,
    primarySaturation: 89,
    primaryLightness: 59,
    overrides: {
      // CYDEcore-aligned dark surface palette: cooler blue-tinted shells and
      // alpha-white hairlines for that "crisp" look across the whole app.
      background: 'rgb(11, 14, 23)',
      backgroundGradient: 'rgb(11, 14, 23)',
      pageBackgroundGradient: 'linear-gradient(to bottom right, rgb(8, 11, 18), rgb(11, 14, 23))',
      // Card surface: ~5 units lifted off page bg, matching the CYDEcore mock.
      // Contrast is generated by this small lift + alpha-white hairline border, not by overshooting it.
      card: 'rgb(15, 20, 32)',
      cardHover: 'rgb(20, 25, 41)',
      popover: 'rgb(20, 25, 41)',
      input: 'rgb(20, 25, 41)',
      muted: 'rgb(20, 25, 41)',
      // Hairline borders catch light better than opaque slate; this is the single biggest
      // contributor to crispness app-wide because `* { border-color: var(--theme-border) }`.
      border: 'rgba(255, 255, 255, 0.07)',
      borderSubtle: 'rgba(255, 255, 255, 0.13)',
      borderStrong: 'rgba(255, 255, 255, 0.22)',
      foreground: 'rgb(240, 242, 247)',
      foregroundMuted: 'rgb(155, 163, 184)',
      foregroundSubtle: 'rgb(79, 88, 115)',
      accent: 'rgb(59, 124, 244)',
      accentHover: 'rgb(77, 140, 245)',
      accentMuted: 'rgba(59, 124, 244, 0.12)',
      accentMutedForeground: 'rgb(102, 153, 255)',
      success: 'rgb(45, 212, 160)',
      warning: 'rgb(245, 166, 35)',
      error: 'rgb(240, 82, 82)',
      headerFooter: 'rgba(11, 14, 23, 0.93)',
    },
  },
  'light': {
    id: 'light',
    name: 'Light',
    category: 'light',
    colorScheme: 'slate',
    primaryHue: 217,
    primarySaturation: 91,
    primaryLightness: 60,
  },
  'tenant-selector': {
    id: 'tenant-selector',
    name: 'Tenant Selector',
    category: 'light',
    colorScheme: 'blue',
    primaryHue: 217,
    primarySaturation: 91,
    primaryLightness: 50,
    overrides: {
      backgroundGradient: 'linear-gradient(to bottom right, rgb(239 246 255), rgb(224 231 255))',
    },
  },
  'dark-slate': {
    id: 'dark-slate',
    name: 'Dark Slate',
    category: 'dark',
    colorScheme: 'slate',
    primaryHue: 222,
    primarySaturation: 47,
    primaryLightness: 11,
  },
  'amoled-black': {
    id: 'amoled-black',
    name: 'Into The Black Hole',
    category: 'dark',
    colorScheme: 'custom',
    primaryHue: 195,  // Cyan hue for accent
    primarySaturation: 100,
    primaryLightness: 50,
    overrides: {
      // True AMOLED black backgrounds
      background: 'hsl(0 0% 0%)',  // Pure black #000000
      pageBackgroundGradient: 'linear-gradient(to bottom right, hsl(0 0% 0%), hsl(0 0% 0%))',
      card: 'hsl(0 0% 3%)',  // Very slightly lighter for cards
      popover: 'hsl(0 0% 5%)',  // Slightly lighter for popovers
      // High contrast borders for visibility on pure black
      border: 'hsl(0 0% 35%)',  // Light grey borders for excellent visibility
      borderSubtle: 'hsl(0 0% 30%)',  // Slightly less visible for subtle borders
      borderStrong: 'hsl(0 0% 40%)',  // More visible for focus states
      // Bright foreground for maximum contrast
      foreground: 'hsl(0 0% 98%)',  // Near white text
      // Accent color (cyan/blue) for excellent visibility
      accent: 'hsl(195 100% 50%)',  // Bright cyan for high contrast
    },
  },
  'material-dark-grey': {
    id: 'material-dark-grey',
    name: 'Material Simple Dark Grey',
    category: 'dark',
    colorScheme: 'gray',
    primaryHue: 217,  // Blue hue for Material Design accent
    primarySaturation: 91,
    primaryLightness: 60,
    overrides: {
      // Material Design dark theme colors
      background: 'hsl(0 0% 7%)',  // #121212 equivalent
      pageBackgroundGradient: 'linear-gradient(to bottom right, hsl(0 0% 6%), hsl(0 0% 7%))',
      card: 'hsl(0 0% 13%)',  // #212121 (Grey 900) equivalent
      popover: 'hsl(0 0% 13%)',  // #212121
      // Visible borders using Material Design grey scale
      border: 'hsl(0 0% 26%)',  // #424242 (Grey 800) equivalent - good visibility
      borderSubtle: 'hsl(0 0% 22%)',  // #424242 slightly darker for subtle borders
      borderStrong: 'hsl(0 0% 30%)',  // Lighter for focus states
      // Material Design text colors
      foreground: 'hsl(0 0% 100%)',  // White text
      // Material Design accent (typically blue)
      accent: 'hsl(217 91% 60%)',  // Material blue accent
    },
  },
  cydecore: {
    id: "cydecore",
    name: "CYDEcore",
    category: "dark",
    colorScheme: "custom",
    primaryHue: 220,
    primarySaturation: 89,
    primaryLightness: 59,
    overrides: {
      background: "rgb(11, 14, 23)",
      backgroundGradient: "rgb(11, 14, 23)",
      pageBackgroundGradient: "rgb(11, 14, 23)",
      card: "rgb(15, 20, 32)",
      popover: "rgb(20, 25, 41)",
      foreground: "rgb(240, 242, 247)",
      foregroundMuted: "rgb(155, 163, 184)",
      foregroundSubtle: "rgb(79, 88, 115)",
      border: "rgba(255, 255, 255, 0.07)",
      borderSubtle: "rgba(255, 255, 255, 0.13)",
      borderStrong: "rgba(255, 255, 255, 0.22)",
      accent: "rgb(59, 124, 244)",
      accentHover: "rgb(77, 140, 245)",
      input: "rgb(20, 25, 41)",
      muted: "rgb(20, 25, 41)",
      success: "rgb(45, 212, 160)",
      warning: "rgb(245, 166, 35)",
      error: "rgb(240, 82, 82)",
      warningBackground: "rgba(245, 166, 35, 0.15)",
      accentMuted: "rgba(59, 124, 244, 0.12)",
      accentMutedForeground: "rgb(102, 153, 255)",
      cardHover: "rgb(26, 32, 53)",
      headerFooter: "rgba(11, 14, 23, 0.93)",
    },
    cssVarExtras: {
      ...CYDECORE_CSS_VAR_MAP,
      "--theme-dashboard-widget-bg": "rgb(15, 20, 32)",
      "--theme-table-row": "rgb(15, 20, 32)",
      "--theme-table-empty": "rgb(11, 14, 23)",
      "--theme-table-header": "rgb(26, 32, 53)",
      "--theme-card-elevated": "rgb(26, 32, 53)",
      "--theme-border-dialog": "rgba(59, 124, 244, 0.25)",
      "--theme-info-pill-bg": "rgba(59, 124, 244, 0.10)",
      "--theme-input-search": "rgb(20, 25, 41)",
      "--theme-select-content-bg": "rgb(15, 20, 32)",
      "--theme-filter-container": "rgb(15, 20, 32)",
      "--theme-mdr-controls-container": "rgb(11, 14, 23)",
      "--ring": "220 89% 59%",
    },
  },
};

/**
 * Type for theme names (automatically generated from registry)
 * 
 * For backward compatibility, this is exported as a union type.
 * In practice, any string that exists in themeRegistry is valid.
 */
export type ThemeName = keyof typeof themeRegistry;

/**
 * Type guard to check if a string is a valid theme name
 * (for runtime validation)
 */
export function isThemeName(value: string): value is ThemeName {
  return value in themeRegistry;
}

/**
 * Get theme definition by ID
 */
export function getThemeDefinition(themeId: string): ThemeDefinition | null {
  return themeRegistry[themeId] || null;
}

/**
 * Get all available theme IDs
 */
export function getAvailableThemes(): string[] {
  return Object.keys(themeRegistry);
}

/**
 * Check if a theme ID is valid
 */
export function isValidTheme(themeId: string): boolean {
  return themeId in themeRegistry;
}

/**
 * Generate themes programmatically (example for scaling to 100+ themes)
 * 
 * Usage:
 *   const generatedThemes = generateThemes(100);
 *   Object.assign(themeRegistry, generatedThemes);
 */
export function generateThemes(count: number): Record<string, ThemeDefinition> {
  const themes: Record<string, ThemeDefinition> = {};
  
  // Generate dark themes
  for (let i = 0; i < Math.floor(count / 2); i++) {
    const hue = 200 + (i * 3) % 160;
    themes[`dark-${i}`] = {
      id: `dark-${i}`,
      name: `Dark Theme ${i + 1}`,
      category: 'dark',
      colorScheme: 'slate',
      primaryHue: hue,
      primarySaturation: 80 + (i % 20),
      primaryLightness: 50 + (i % 10),
    };
  }
  
  // Generate light themes
  for (let i = 0; i < Math.ceil(count / 2); i++) {
    const hue = 180 + (i * 4) % 180;
    themes[`light-${i}`] = {
      id: `light-${i}`,
      name: `Light Theme ${i + 1}`,
      category: 'light',
      colorScheme: 'blue',
      primaryHue: hue,
      primarySaturation: 70 + (i % 30),
      primaryLightness: 60 + (i % 15),
    };
  }
  
  return themes;
}
